import { IFavoriteProduct } from "interfaces/IProduct";
import { useEffect, useRef, useState } from "react";
import "../../../../../styles/base.scss"
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faHeart,
    faShoppingCart,
} from "@fortawesome/free-solid-svg-icons";
import { IconButton } from "@mui/material";

/**
 * Custom props for the favorite card component
 */
interface Props {
    /**
     * The favorite product information
     */
    product: IFavoriteProduct;
    /**
     * Function that triggers the modal
     */
    showModal: (productId: number) => void;
    /**
     * Function that attempts a wish list product removal
     */
    handleRemove: (productId: number) => void;
    /**
     * Whether the current view is mobile
     */
    isMobile: boolean;
}

/**
 * Component of a card representing a favorite product
 */
export const FavoriteCard = ({ product, showModal, handleRemove, isMobile }: Props) => {
    const hoverRef = useRef<boolean>(false);
    const intervalRef = useRef<number | null>(null);
    const [index, setIndex] = useState<number>(0);
    const [opacity, setOpacity] = useState(1);
    const { product_id, product_name, price, images, sizes, status } = product;

    const onRemove = (productId: number) => handleRemove(productId);

    const handleCart = () => {

    }

    // Verificar si el producto está disponible
    const isProductAvailable = status && images && images.length > 0;

    /**
     * Use Effect that changes the image on hovering the favorite card
     */
    useEffect(() => {
        if (!isProductAvailable) return; // No cambiar imágenes si el producto no está disponible
        
        intervalRef.current = window.setInterval(() => {
            if (hoverRef.current) {
                setOpacity(0);

                setTimeout(() => {
                    setIndex(prev => (prev + 1) % images.length);
                    setOpacity(1);
                }, 500);
            }
        }, 2000);

        return () => clearInterval(intervalRef.current!);
    }, [isProductAvailable, images]);

    return (
        <div
            className="favorite-card"
            onClick={() => isProductAvailable ? showModal(product.product_id) : null}
            onMouseEnter={() => hoverRef.current = true}
            onMouseLeave={() => hoverRef.current = false}
            style={{ 
                cursor: isProductAvailable ? 'pointer' : 'default',
                opacity: isProductAvailable ? 1 : 0.7 
            }}
        >
            <div className="favorite-card-image">
                {isProductAvailable && images?.length > 0 ? (
                    <img
                        className="favorite-card-image"
                        src={images[index] || ""}
                        alt={product_name}
                        style={{
                            opacity,
                            transition: "opacity 0.5s ease-in-out"
                        }}
                    />
                ) : (
                    <div 
                        className="favorite-card-image"
                        style={{
                            backgroundColor: "#f8f9fa",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            border: "1px solid #dee2e6"
                        }}
                    >
                        <div style={{ textAlign: "center", color: "#6c757d" }}>
                            <div style={{ fontSize: "3rem", marginBottom: "0.5rem" }}>📦</div>
                            <div style={{ fontSize: "0.9rem", fontWeight: "bold" }}>Producto no disponible</div>
                        </div>
                    </div>
                )}
            </div>
            <div className="favorite-card-name text-center" >
                <h3 title={product_name} className=" m-1 text-wrap" style={{ fontSize: isMobile ? "16px" : "20px", fontWeight: "bold" }}>{product_name}</h3>
            </div>
            {!isMobile && (
                <div className="favorite-card-sizes" style={{ minHeight: isProductAvailable ? "auto" : "40px" }}>
                    {isProductAvailable ? (
                        <div className="d-flex justify-content-start align-content-center m-2 flex-nowrap">
                            <span className="favorite-card-tag me-3">Tallas</span>
                            <div className="d-flex justify-content-between flex-wrap">
                                {sizes?.map((size) => (
                                    <strong key={size} className="me-3 size-tag">
                                        {size}
                                    </strong>
                                ))}
                            </div>
                        </div>
                    ) : (
                        <div className="d-flex justify-content-start align-content-center m-2 flex-nowrap">
                            <span className="favorite-card-tag me-3">Tallas</span>
                            <div className="d-flex justify-content-between flex-wrap">
                                <span style={{ color: "#6c757d", fontStyle: "italic" }}>No disponible</span>
                            </div>
                        </div>
                    )}
                </div>
            )}
            <div className="d-flex justify-content-between align-items-center m-2" style={{ minHeight: "40px" }}>
                {isProductAvailable ? (
                    <strong className="favorite-card-price">
                        ${price || 0}
                    </strong>
                ) : (
                    <strong style={{ color: "#dc3545", fontSize: "0.9rem" }}>
                        Agotado
                    </strong>
                )}
                <div className="favorite-card-icons d-flex flex-nowrap align-items-center">
                    <OverlayTrigger
                        trigger={["hover", "focus"]}
                        placement="top"
                        overlay={
                            <Tooltip
                                id="favorite-tooltip"
                                className="custom-tooltip-client"
                            >
                                Quitar este producto de mi lista de favoritos.
                            </Tooltip>}
                        rootClose
                    >
                        <IconButton
                            size={isMobile ? "medium" : "small"}
                            style={{ color: "#997404" }}
                            onClick={(e) => {
                                e.stopPropagation();
                                onRemove(product_id);
                            }}
                        >
                            <FontAwesomeIcon
                                icon={faHeart}
                                style={{ 
                                    cursor: "pointer",
                                    fontSize: isMobile ? "1.5rem" : "1rem"
                                }}
                            />
                        </IconButton>
                    </OverlayTrigger>
                    {!isMobile && isProductAvailable && (
                        <OverlayTrigger
                            trigger={["hover", "focus"]}
                            placement="top"
                            overlay={
                                <Tooltip
                                    id="cart-tooltip"
                                    className="custom-tooltip-client"
                                >
                                    Agregar a mi carrito de compras
                                </Tooltip>}
                            rootClose
                        >
                            <IconButton 
                                size="small" 
                                style={{ color: "#997404" }} 
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleCart();
                                }}
                            >
                                <FontAwesomeIcon
                                    icon={faShoppingCart}
                                    style={{ cursor: "pointer" }}
                                />
                            </IconButton>
                        </OverlayTrigger>
                    )}
                </div>
            </div>
        </div>
    )
}
